import React, { useState, useRef } from 'react';
import { colors, fonts, radius } from '../../theme';
import { COMMON_MACHINES, MUSCLE_KINDS, suggestedMuscleKind, validateScan } from '../../utils/bodyStats';
import { addScan, correctScan, newRequestId } from '../../services/bodyScanService';
import { Button, Notice, mono } from './parts';

const OTHER = '__other__';
// For "Not saved yet": the fields in the order they appear, their ids and their names
const FIELD_ORDER = ['measuredOn', 'machine', 'weight', 'fat', 'muscle', 'muscleKind', 'form'];
const FIELD_IDS = { measuredOn: 'scan-date', machine: 'scan-machine', weight: 'scan-weight', fat: 'scan-fat', muscle: 'scan-muscle', muscleKind: 'scan-muscle-kind', form: 'scan-weight' };
const FIELD_NAMES = { measuredOn: 'the date of the scan', machine: 'the machine', weight: 'weight', fat: 'body fat', muscle: 'muscle', muscleKind: 'the type of muscle measurement', form: 'at least one number' };
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const str = (v) => (v === null || v === undefined ? '' : String(v));

const field = {
  width: '100%', minHeight: 48, boxSizing: 'border-box', background: colors.bg, color: colors.text,
  border: `1px solid ${colors.borderStrong}`, borderRadius: radius.sm, padding: 12, fontSize: 15, fontFamily: fonts.body,
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 };
const helper = { fontSize: 11, color: colors.textMuted, marginTop: 4 };
const errorStyle = { fontSize: 12, color: colors.text, marginTop: 4, borderLeft: `2px solid ${colors.text}`, paddingLeft: 6 };

/**
 * Typed entry, and correcting a scan (SPEC screens 08 and 09). Date, machine (a common one or "Other"
 * with a name), weight, body fat and muscle (with what the sheet calls it); at least one number. The
 * client's previous machine is offered first, and a different machine gets a calm note.
 */
export default function ScanForm({ clientId, scan = null, lastMachine = null, onSaved, onCancel, prefill = null, onRetake = null }) {
  // prefill: what was read from a photo ({ measured_on, machine, weight_kg, body_fat_pct, muscle_kg,
  // muscle_kind, not_used }); every value stays editable and the person saves it themselves.
  const fromPhoto = !!prefill;
  const read = prefill || {};
  // The client's own machines first (their last one, and this scan's), then the common ones
  const names = [...new Set([read.machine, scan?.machine, lastMachine, ...COMMON_MACHINES.map((m) => m.name)].filter(Boolean))];
  // From a photo, a machine the sheet does not clearly show stays empty for the person to choose, never
  // their last machine in its place (Codex review of step 5, M1). A date it does not show is today, said
  // so under the field (the owner, 17 October 2026: most scans are added the day they are taken).
  const startMachine = fromPhoto ? (read.machine ?? '') : (scan?.machine ?? lastMachine ?? '');
  const [measuredOn, setMeasuredOn] = useState(fromPhoto ? (read.measured_on ?? todayIso()) : (scan?.measured_on ?? todayIso()));
  const [choice, setChoice] = useState(startMachine);
  const [otherName, setOtherName] = useState('');
  const [weight, setWeight] = useState(str(fromPhoto ? read.weight_kg : scan?.weight_kg));
  const [fat, setFat] = useState(str(fromPhoto ? read.body_fat_pct : scan?.body_fat_pct));
  const [muscle, setMuscle] = useState(str(fromPhoto ? read.muscle_kg : scan?.muscle_kg));
  const [muscleKind, setMuscleKind] = useState(read.muscle_kind ?? scan?.muscle_kind ?? suggestedMuscleKind(startMachine));
  // What the sheet calls the muscle, once read from it or chosen by the person, is never changed by
  // choosing another machine: that would change what the number means (Codex review of step 5, M2)
  const kindSettled = useRef(fromPhoto && !!read.muscle_kind);
  // Under each value read from a photo: where it came from, that the person changed it, or that it was not found
  const sheetNote = (value, current, missing = "Not found on the sheet: type it if it's there") => {
    if (!fromPhoto) return null;
    if (value === null || value === undefined) return missing;
    return String(current).trim() === String(value) ? 'From your sheet' : `Changed by you (the sheet read ${value})`;
  };
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const requestId = useRef(newRequestId()); // a retry of the same scan adds nothing

  const machine = choice === OTHER ? otherName : choice;
  const differentMachine = !scan && lastMachine && machine && machine.trim() !== lastMachine;

  const save = async () => {
    const fields = { measuredOn, machine, weight, fat, muscle, muscleKind };
    const found = validateScan(fields);
    setErrors(found);
    if (Object.keys(found).length) {
      // The messages sit by their fields, which on a phone can be off the screen: go to the first one
      const first = FIELD_ORDER.find((k) => found[k]);
      const el = first && document.getElementById(FIELD_IDS[first]);
      if (el) { el.scrollIntoView?.({ block: 'center' }); el.focus?.({ preventScroll: true }); }
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const saved = scan ? await correctScan(scan, fields) : await addScan(clientId, { ...fields, entryMethod: fromPhoto ? 'photo_confirmed' : 'typed' }, requestId.current);
      onSaved(saved);
    } catch (e) {
      setSaveError(e.message || 'Could not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const numberInput = (id, label, value, set, unit, error, note = null) => (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input id={id} type="number" inputMode="decimal" step="0.1" value={value} onChange={(e) => set(e.target.value)}
          aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
          style={{ ...field, ...mono, paddingRight: 56 }} />
        <span aria-hidden="true" style={{ ...mono, position: 'absolute', right: 12, top: 14, fontSize: 13, color: colors.textMuted }}>{unit}</span>
      </div>
      {note && !error && <div style={helper}>{note}</div>}
      {error && <div id={`${id}-error`} role="alert" style={errorStyle}>{error}</div>}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label htmlFor="scan-date" style={labelStyle}>Date of scan</label>
        <input id="scan-date" type="date" value={measuredOn} max={todayIso()} onChange={(e) => setMeasuredOn(e.target.value)} style={field}
          onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* the phone opens its own */ } }} />
        {!errors.measuredOn && fromPhoto && <div style={helper}>{sheetNote(read.measured_on, measuredOn, read.measured_on ? null : "Not on the sheet: set to today. Change it if the scan was another day")}</div>}
        {errors.measuredOn && <div role="alert" style={errorStyle}>{errors.measuredOn}</div>}
      </div>
      <div>
        <label htmlFor="scan-machine" style={labelStyle}>Machine</label>
        <select id="scan-machine" value={choice} style={field}
          onChange={(e) => { setChoice(e.target.value); if (!scan && !kindSettled.current) setMuscleKind(suggestedMuscleKind(e.target.value)); }}>
          <option value="" disabled>Choose the machine</option>
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value={OTHER}>Other…</option>
        </select>
        {choice === OTHER && (
          <input aria-label="Machine name" placeholder="Machine name, e.g. Seca mBCA 555" value={otherName} maxLength={80}
            onChange={(e) => setOtherName(e.target.value)} style={{ ...field, marginTop: 8 }} />
        )}
        {fromPhoto && <div style={helper}>{read.machine ? (machine === read.machine ? 'From your sheet' : `Changed by you (the sheet read ${read.machine})`) : 'Not found on the sheet: choose your machine'}</div>}
        <div style={helper}>Use the same machine each time if you can: trends compare scans from the same machine.</div>
        {errors.machine && <div role="alert" style={errorStyle}>{errors.machine}</div>}
      </div>
      {differentMachine && (
        <Notice compact>This is a different machine from your last scan ({lastMachine}). Changes may partly be the machine.</Notice>
      )}
      {fromPhoto && <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>Check each number against your sheet before saving.</div>}
      {numberInput('scan-weight', 'Weight', weight, setWeight, 'kg', errors.weight, sheetNote(read.weight_kg, weight))}
      {numberInput('scan-fat', 'Body fat', fat, setFat, '%', errors.fat, sheetNote(read.body_fat_pct, fat))}
      {numberInput('scan-muscle', 'Muscle', muscle, setMuscle, 'kg', errors.muscle, sheetNote(read.muscle_kg, muscle))}
      {fromPhoto && read.not_used?.length > 0 && (
        <Notice compact>Some things on the sheet could not be used ({read.not_used.join(', ')}). Type them if they are there.</Notice>
      )}
      {muscle !== '' && (
        <div>
          <label htmlFor="scan-muscle-kind" style={labelStyle}>Type of muscle measurement</label>
          <select id="scan-muscle-kind" value={muscleKind} onChange={(e) => { kindSettled.current = true; setMuscleKind(e.target.value); }} style={field}>
            {Object.entries(MUSCLE_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={helper}>As printed on your sheet. Machines measure muscle differently, so only the same type is compared.</div>
          {errors.muscleKind && <div role="alert" style={errorStyle}>{errors.muscleKind}</div>}
        </div>
      )}
      <div style={helper}>Leave anything your sheet does not show empty. At least one number.</div>
      {errors.form && <div role="alert" style={errorStyle}>{errors.form}</div>}
      {Object.keys(errors).length > 0 && (
        <div role="alert" style={errorStyle}>Not saved yet. Check: {FIELD_ORDER.filter((k) => errors[k]).map((k) => FIELD_NAMES[k]).join(', ')}.</div>
      )}
      {saveError && <div role="alert" style={errorStyle}>{saveError}</div>}
      <div style={{ display: 'grid', gap: 4, marginTop: 12 }}>
        <Button kind="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        {onRetake ? <Button kind="quiet" onClick={onRetake}>Retake photo</Button> : <Button kind="quiet" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}
